import { Grid, Typography, Stack, useMediaQuery, Box } from "@mui/material";
import {
  ArrayField,
  Datagrid,
  FunctionField,
  ReferenceField,
  SimpleShowLayout,
  TextField,
  useNotify,
  useRecordContext,
  SingleFieldList,
  ChipField,
  ReferenceArrayField,
  BooleanField,
  Labeled,
  SelectField,
} from "react-admin";
import { Button } from "@mui/material";

import { getFile, previewFileFromUrl } from "../../../server/components/DownloadFile";
import { CampaignSubTypes, CampaignTypes, DynamicDeclarations } from "utils/constants";
import SchoolGrades from "./SchoolGrades/SchoolGrades";
import { settings } from "app";
import ExamSlotDialog from "./components/ExamSlotDialog";
import SecuredComponent from "server/security/SecuredComponent";
import permissions from "app/permissions";
import SpecialitiesPrioritySlotDialog from "./components/SpecialitiesPriorityDialog";
import AdmissionAfterEducationModal from "./components/AdmissionAfterEducationModal";
import DescriptionIcon from "@mui/icons-material/Description";
import PreparatoryClass from "./components/PreparatoryClass";
import CoursesSection from "./components/CoursesSection";
import { getGradeColor, getTheme } from "customStyles/utils";
import RankingAccordionItem from "./components/RankingAccordion";

import ChangeSpecialityDialog from "./components/ChangeSpecialityDialog";
import ChangeInstructionLanguages from "./components/ChangeInstructionLanguagesModal";
import ChangeScoreComponentsModal from "./components/ChangeScoreComponentsModal";
import DeleteSpecialityModal from "./components/DeleteSpecialityModal";
import ChangePaidEducationModal from "./components/ChangePaidEducationModal";
import ChangeReinstatesRightsModal from "./components/ChangeReinstatesRightsModal";
import { candidateRequests } from "api/candidateApi";
import { administrationRequests } from "api/administrationApi";
import EditExamGradeDialog from "./components/EditExamGradeDialog";
import ExamProtocolButton from "./components/ExamProtocolButton";
import { SectionTitle } from "features/SectionTitle";

const CandidacyData = ({ isForeigner }) => {
  const theme = getTheme(settings.tenant);
  const record = useRecordContext();

  const notify = useNotify();
  const isMobile = useMediaQuery(theme => theme.breakpoints.down("md"));

  const isRegularCampaign = record?.campaignSubTypeId === CampaignSubTypes.Regular;
  const isPaidCampaign = record?.campaignSubTypeId === CampaignSubTypes.Paid;
  const isHospitalContractCampaign = record?.campaignSubTypeId === CampaignSubTypes.HospitalContract;

  const isApprovedCandidate = record?.stateId === 1;
  const activeExams = record?.exams?.filter(exam => !exam?.unenrolled) || [];

  const previewForeignerNote = () => {
    const declarationId = record?.preparatoryClass
      ? DynamicDeclarations.ForeignerPreparatoryNoteId
      : DynamicDeclarations.ForeignerNoteId;

    previewFileFromUrl(
      `${settings.gatewayApi}/${candidateRequests.getCandidacyDynamicDeclaration(record.id, declarationId)}`,
      null,
      notify,
    );
  };

  return (
    <SimpleShowLayout>
      <SectionTitle
        title="Данни за кандидатурата"
        textAlign="center"
        fontSize={18}
        sx={{ mt: 3 }}
      />
      <Grid
        container
        spacing={3}
        sx={isMobile ? settings.style.shadowBoxMobile : settings.style.shadowBox}
      >
        {record?.campaignTypeId !== CampaignTypes.PreparatoryCourses && (
          <Grid
            item
            xs={12}
            md={4}
          >
            <Labeled label="Вх. Номер">
              <TextField
                source="number"
                emptyText="-"
              />
            </Labeled>
          </Grid>
        )}
        <Grid
          item
          xs={12}
          md={4}
        >
          <Labeled label="Под тип">
            <ReferenceField
              source="campaignTypeId"
              reference={candidateRequests.getCampaignTypesSelect()}
            >
              <TextField
                label="Под тип"
                source="name"
              />
            </ReferenceField>
          </Labeled>
        </Grid>
        <Grid
          item
          xs={12}
          md={4}
          sx={{ display: "grid", width: "min-content" }}
        >
          {!isForeigner && record?.campaignTypeId !== CampaignTypes.PreparatoryCourses ? (
            <>
              <Labeled label="Кандидатства за платено обучение?">
                <SelectField
                  mb={2}
                  source="onlyPaidEducation"
                  choices={[
                    { id: true, name: "Да" },
                    { id: false, name: "Не" },
                  ]}
                />
              </Labeled>
              <SecuredComponent permission={permissions.canChangeCandidacyPaidEducation}>
                {isRegularCampaign && <ChangePaidEducationModal record={record} />}
              </SecuredComponent>
            </>
          ) : /* //Бутон */

          null}
        </Grid>
        <Grid
          item
          xs={12}
          md={4}
        >
          <Labeled label="Дата на кандидатура">
            <TextField source="createdOnFormatted" />
          </Labeled>
        </Grid>
        <Grid
          item
          xs={12}
          md={4}
        >
          <Labeled label="Избрано направление">
            <ReferenceField
              source="directionId"
              reference={administrationRequests.getDirectionsSelect()}
            >
              <TextField
                label="Дирекция"
                source="name"
              />
            </ReferenceField>
          </Labeled>
        </Grid>
        {isHospitalContractCampaign && (
          <Grid
            item
            xs={12}
            md={4}
          >
            <Labeled label="Болница">
              <ReferenceField
                source="hospitalityId"
                reference={administrationRequests.getHospitalitiesSelect()}
                emptyText="-"
              >
                <TextField
                  label="Болница"
                  source="nameBg"
                />
              </ReferenceField>
            </Labeled>
          </Grid>
        )}
        {record?.campaignTypeId !== CampaignTypes.PreparatoryCourses && (
          <Grid
            item
            xs={12}
            md={4}
            sx={{ display: "grid", width: "min-content" }}
          >
            <Labeled label="Език на обучение">
              <ReferenceField
                source="instructionLanguageId"
                reference={administrationRequests.getInstructionLanguagesSelect()}
              >
                <TextField
                  label="Език на обучение"
                  source="name"
                />
              </ReferenceField>
            </Labeled>
            {/* //Модал */}
            {record?.campaignTypeId !== CampaignSubTypes.Preliminary && (
              <SecuredComponent permission={permissions.canUpdateCandidacyInstructionLanguages}>
                <ChangeInstructionLanguages record={record} />
              </SecuredComponent>
            )}
          </Grid>
        )}

        {isForeigner ? (
          <Grid
            item
            xs={12}
            md={4}
          >
            <PreparatoryClass
              candidacyId={record?.id}
              preparatoryClass={record?.preparatoryClass}
              isLatestRankingApproved={record?.isLatestRankingApproved}
            />
          </Grid>
        ) : null}
        {isForeigner ? (
          <>
            <Grid
              item
              xs={12}
              md={4}
            >
              <Labeled label="Трансфер от друг университет">
                <BooleanField
                  source="isFromAnotherUniversity"
                  defaultValue={false}
                />
              </Labeled>
            </Grid>
            <Grid
              item
              xs={12}
              md={4}
            >
              <Labeled label="Участва в класиране">
                <BooleanField
                  source="participatesInRanking"
                  defaultValue={true}
                />
              </Labeled>
            </Grid>
          </>
        ) : null}
      </Grid>
      {/* ------------------------------------------------------------------- */}

      {(isRegularCampaign || isPaidCampaign || isHospitalContractCampaign) && (
        <Grid
          container
          spacing={3}
          sx={isMobile ? settings.style.shadowBoxMobile : settings.style.shadowBox}
        >
          <Grid
            item
            xs={12}
            md={6}
          >
            <Typography
              color={"#4E2683"}
              fontWeight={600}
            >
              Балове от класиране
            </Typography>
            <ArrayField source="specialitiesScores">
              <Datagrid
                rowClick={false}
                bulkActionButtons={false}
                sx={settings.style.secondaryDatagrid}
              >
                <ReferenceField
                  label="Специалност"
                  source="specialityId"
                  reference={administrationRequests.getSpecialitiesSelect()}
                >
                  <TextField
                    label="Име"
                    source="name"
                    emptyText="-"
                  />
                </ReferenceField>

                <TextField
                  label="Бал"
                  source="scoreFormatted"
                  emptyText="-"
                />
              </Datagrid>
            </ArrayField>
          </Grid>

          <Grid
            item
            xs={12}
            md={6}
          >
            <Typography
              color={"#4E2683"}
              fontWeight={600}
            >
              Класирания резултати
            </Typography>
            <RankingAccordionItem />
          </Grid>

          <Grid
            item
            xs={12}
          >
            <Typography
              color={"#4E2683"}
              fontWeight={600}
            >
              Избрани специалности
            </Typography>
            <ArrayField source="specialities">
              <Datagrid
                rowClick={false}
                bulkActionButtons={false}
                sx={settings.style.secondaryDatagrid}
              >
                <ReferenceField
                  label="Име"
                  source="specialityId"
                  reference={administrationRequests.getSpecialitiesSelect()}
                >
                  <TextField
                    label="Име"
                    source="name"
                    emptyText="-"
                  />
                </ReferenceField>
                {!isForeigner ? (
                  <TextField
                    label="Желание"
                    source="priority"
                    emptyText="-"
                  />
                ) : null}
                <ReferenceField
                  label="Тип на обучение"
                  source="studyTypeId"
                  reference={administrationRequests.getStudyTypesSelect()}
                >
                  <TextField
                    label="Тип на обучение"
                    source="name"
                    emptyText="-"
                  />
                </ReferenceField>
                <BooleanField
                  textAlign="center"
                  label="Възстановяване на права"
                  source="reinstatesRights"
                />
                {record?.specialities.length === 1 && (
                  <SecuredComponent permission={permissions.canUpdateCandidacyReinstatesRight}>
                    <FunctionField
                      label=""
                      render={specialty => (
                        <ChangeReinstatesRightsModal
                          record={record}
                          specialty={specialty}
                        />
                      )}
                    />
                  </SecuredComponent>
                )}

                {/* //Бутон */}
                {!isForeigner ? (
                  <FunctionField
                    label={"Свали документ"}
                    render={speciality =>
                      speciality.reinstatesRightsDocumentId ? (
                        <Typography
                          sx={{ textDecoration: "underline", fontSize: "14px", cursor: "pointer" }}
                          onClick={() => getFile(speciality.reinstatesRightsDocumentId, notify ? notify : "")}
                        >
                          {`file-${speciality.reinstatesRightsDocumentId}.pdf`}
                        </Typography>
                      ) : (
                        "-"
                      )
                    }
                  />
                ) : null}
                <SecuredComponent permission={permissions.canManageCandidacySpecialities}>
                  <FunctionField
                    label={" "}
                    render={speciality => (
                      <DeleteSpecialityModal
                        speciality={speciality}
                        record={record}
                      />
                    )}
                  />
                </SecuredComponent>
              </Datagrid>
              {!isForeigner ? (
                <SecuredComponent permission={permissions.canChangeSpecialitiesPriority}>
                  <SpecialitiesPrioritySlotDialog
                    candidacyId={record.id}
                    specialities={record.specialities}
                    disabled={record?.hasApprovedRanking}
                  />
                </SecuredComponent>
              ) : null}
              {/* //Модал */}
              <SecuredComponent permission={permissions.canManageCandidacySpecialities}>
                <ChangeSpecialityDialog
                  candidacyId={record.id}
                  disabled={record?.hasApprovedRanking}
                />
              </SecuredComponent>
            </ArrayField>
          </Grid>
        </Grid>
      )}

      {/* ---------------------------------------------------------------------------------- */}

      {isRegularCampaign && isForeigner && (
        <Grid sx={{ display: "flex", items: "center", justifyContent: "center" }}>
          <Button
            variant="contained"
            size="small"
            color="primary"
            startIcon={<DescriptionIcon />}
            sx={{ fontSize: 12, py: 0.8, px: 2 }}
            onClick={previewForeignerNote}
          >
            Бележка за явяване на изпит
          </Button>
        </Grid>
      )}

      {/* ------------------------------------------------------------------------------------------------ */}

      {activeExams.length > 0 ? (
        <Grid
          container
          spacing={3}
          sx={isMobile ? settings.style.shadowBoxMobile : settings.style.shadowBox}
        >
          <Grid
            item
            xs={12}
          >
            <Typography
              color={"#4E2683"}
              fontWeight={600}
            >
              Избрани изпити
            </Typography>
            <ArrayField
              record={{ ...record, exams: activeExams }}
              source="exams"
            >
              <Datagrid
                rowClick={false}
                bulkActionButtons={false}
                sx={settings.style.secondaryDatagrid}
              >
                <ReferenceField
                  label="Име"
                  source="examId"
                  reference={administrationRequests.getExamsSelect()}
                >
                  <TextField
                    label="Изпит"
                    source="name"
                    emptyText="-"
                  />
                </ReferenceField>
                <ReferenceField
                  label="Разпределен"
                  source="slotId"
                  reference={candidateRequests.getExamSlotsSelect()}
                >
                  <FunctionField
                    label="Разпределен"
                    render={rec => {
                      const location = [rec.roomName, rec.buildingName].filter(Boolean).join(" - ");
                      const time =
                        rec.startTime && rec.endTime
                          ? ` (${rec.startTime} - ${rec.endTime})`
                          : rec.startTime
                            ? ` (${rec.startTime})`
                            : "";

                      const result = `${location}${time}`.trim();
                      return result || "-";
                    }}
                    emptyText="-"
                  />
                </ReferenceField>
                <FunctionField
                  label="Оценка"
                  render={rec => (
                    <TextField
                      color={getGradeColor(rec.grade)}
                      label="Оценка"
                      source="grade"
                      emptyText="-"
                    />
                  )}
                />
                <FunctionField
                  label="Точки"
                  render={rec => (
                    <TextField
                      color={getGradeColor(rec.grade)}
                      label="Точки"
                      source="points"
                      emptyText="-"
                    />
                  )}
                />

                <Stack
                  spacing={1}
                  direction="column"
                >
                  {!record?.isLatestRankingApproved && (
                    <SecuredComponent permission={permissions.canChangeCandidacyExamGrade}>
                      <FunctionField
                        render={rec =>
                          rec.grade && (
                            <EditExamGradeDialog
                              candidacyId={record?.id}
                              campaignExamId={rec.campaignExamId}
                              grade={rec?.grade}
                              disabled={!isApprovedCandidate}
                            />
                          )
                        }
                      />
                    </SecuredComponent>
                  )}
                  <SecuredComponent permission={permissions.canChangeCandidacyExamSlot}>
                    <FunctionField
                      render={rec =>
                        !rec.examPassed && (
                          <ExamSlotDialog
                            campaignExamId={rec.campaignExamId}
                            candidacyId={record?.id}
                            slotId={rec.slotId}
                            disabled={record?.hasApprovedRanking || !isApprovedCandidate}
                          />
                        )
                      }
                    />
                  </SecuredComponent>
                  <FunctionField
                    render={rec =>
                      !rec.unenrolled &&
                      !rec.examPassed && (
                        <ExamProtocolButton
                          candidacyId={record?.id}
                          campaignExamId={rec.campaignExamId}
                          disabled={!isApprovedCandidate}
                        />
                      )
                    }
                  />
                </Stack>
              </Datagrid>
            </ArrayField>
          </Grid>
        </Grid>
      ) : null}

      {/* --------------------------------------------------------------------------------------------------------------- */}

      {/* Курсове */}
      {record?.campaignTypeId === CampaignTypes.PreparatoryCourses && <CoursesSection />}

      {/* --------------------------------------------------------------------------------------------------------------- */}

      {isRegularCampaign && !isForeigner && (
        <Grid
          container
          spacing={3}
          sx={isMobile ? settings.style.shadowBoxMobile : settings.style.shadowBox}
        >
          <Grid
            item
            xs={12}
            md={6}
          >
            <Typography
              color={"#4E2683"}
              fontWeight={600}
            >
              Оценки от предварителни изпити
            </Typography>
            <ArrayField source="preliminaryExams">
              <Datagrid
                rowClick={false}
                bulkActionButtons={false}
                sx={settings.style.secondaryDatagrid}
              >
                <ReferenceField
                  label="Име"
                  source="examId"
                  reference={administrationRequests.getExamsSelect()}
                >
                  <TextField
                    label="Изпит"
                    source="name"
                    emptyText="-"
                  />
                </ReferenceField>
                <FunctionField
                  label="Оценка"
                  render={rec => (
                    <TextField
                      color={+rec.grade === 2 ? theme.palette.error.primary : null}
                      label="Оценка"
                      source="grade"
                      emptyText="-"
                    />
                  )}
                />
                <FunctionField
                  label="Точки"
                  render={rec => (
                    <TextField
                      color={+rec.grade === 2 ? theme.palette.error.primary : null}
                      label="Точки"
                      source="points"
                      emptyText="-"
                    />
                  )}
                />
              </Datagrid>
            </ArrayField>
          </Grid>

          <Grid
            item
            xs={12}
            md={6}
          >
            <FunctionField
              render={rec => (
                <SchoolGrades
                  candidacyId={record?.id}
                  userId={rec?.userId}
                  hasApprovedRanking={rec?.hasApprovedRanking}
                />
              )}
            />
          </Grid>

          <Grid
            item
            xs={12}
            md={6}
            sx={{ display: "flex", flexDirection: "column", alignItems: "flex-start", gap: 3 }}
          >
            {record?.admissionAfterEducationLevelId && (
              <Box>
                <Typography
                  variant="subtitle1"
                  fontWeight={"bold"}
                  color={"#4E2683"}
                >
                  Добавен прием след:
                </Typography>
                <ReferenceField
                  source="admissionAfterEducationLevelId"
                  reference={administrationRequests.getAdmissionAfterEducationLevelsSelect()}
                >
                  <TextField
                    sx={{ fontSize: "16px" }}
                    name=""
                    source="name"
                  />
                </ReferenceField>
              </Box>
            )}

            <AdmissionAfterEducationModal
              existAdd={record?.admissionAfterEducationLevelId ? true : false}
              candidacyId={record?.id}
              disabled={record?.hasApprovedRanking}
            />
          </Grid>

          <Grid
            item
            xs={12}
            md={6}
          >
            <Typography
              color={"#4E2683"}
              fontWeight={600}
            >
              Диплома от средно образование
            </Typography>
            <ArrayField source="diplomaDocumentIds">
              <Datagrid
                rowClick={false}
                bulkActionButtons={false}
                sx={settings.style.secondaryDatagrid}
              >
                <FunctionField
                  label={"Свали документ"}
                  render={diplomaDocumentId => (
                    <Typography
                      sx={{ textDecoration: "underline", fontSize: "14px", cursor: "pointer" }}
                      onClick={() => getFile(diplomaDocumentId, notify ? notify : "")}
                    >
                      {`file-${diplomaDocumentId}.pdf`}
                    </Typography>
                  )}
                />
              </Datagrid>
            </ArrayField>
          </Grid>

          {record.scoreComponentIds.length > 0 && (
            <Grid
              item
              xs={12}
              md={6}
            >
              <Labeled label="Избрани компоненти за балообразуване">
                <ReferenceArrayField
                  source="scoreComponentIds"
                  reference={candidateRequests.getScoreComponentsSelect()}
                >
                  <SingleFieldList linkType={false}>
                    <ChipField source="nameBg" />
                  </SingleFieldList>
                </ReferenceArrayField>
              </Labeled>
              <SecuredComponent permission={permissions.canUpdateCandidacyScoreComponents}>
                <ChangeScoreComponentsModal record={record} />
              </SecuredComponent>
            </Grid>
          )}
        </Grid>
      )}
    </SimpleShowLayout>
  );
};
export default CandidacyData;
